import { useState } from 'react';
import { Box, Modal, Stack, UnstyledButton, Group, Text } from '@mantine/core';
import { BACKENDS, getBackendMode, setBackendMode } from '../../utils/backendConfig';

const MODE_COLOR = { local: '#7C3AED', dev: '#D97706', prod: '#2563EB' } as const;

/**
 * Backend-mode badge + picker modal. The badge is hidden on `prod` (only
 * shows for dev/local so prod users never see it). The modal can also be
 * opened externally via the controlled `open`/`onOpenChange` props — used
 * e.g. for a hidden 20-tap-logo easter egg on prod.
 */
export function BackendSwitcher({
  position = 'top-left',
  open: controlledOpen,
  onOpenChange,
}: {
  position?: 'top-left' | 'top-right' | 'inline';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
} = {}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [current, setCurrent] = useState(getBackendMode);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (next: boolean) => {
    if (next) setCurrent(getBackendMode());
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  const badge = current === 'prod' ? null : (
    <Box
      onClick={() => setOpen(true)}
      style={{
        background: MODE_COLOR[current],
        color: '#fff',
        fontSize: 10,
        fontWeight: 700,
        padding: '3px 8px',
        borderRadius: 20,
        letterSpacing: '0.06em',
        lineHeight: '14px',
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      {current.toUpperCase()}
    </Box>
  );

  const wrappedBadge = !badge
    ? null
    : position === 'inline'
      ? badge
      : (
        <Box
          style={{
            position: 'fixed',
            top: 12,
            [position === 'top-left' ? 'left' : 'right']: 12,
            zIndex: 100,
          }}
        >
          {badge}
        </Box>
      );

  return (
    <>
      {wrappedBadge}
      <Modal
        opened={open}
        onClose={() => setOpen(false)}
        title="Backend Server"
        size="sm"
        styles={{ title: { fontWeight: 600 } }}
      >
        <Stack gap="xs">
          {(Object.entries(BACKENDS) as [keyof typeof BACKENDS, string][]).map(([mode, url]) => {
            const isActive = current === mode;
            const color = MODE_COLOR[mode];
            return (
              <UnstyledButton
                key={mode}
                onClick={() => { setOpen(false); if (mode !== current) setBackendMode(mode); }}
                style={{
                  padding: '10px 14px',
                  borderRadius: 8,
                  border: `2px solid ${isActive ? color : '#27272A'}`,
                  background: isActive ? `${color}22` : 'transparent',
                  transition: 'border-color 0.15s',
                }}
              >
                <Group gap="xs" mb={2}>
                  <Text size="xs" fw={700} tt="uppercase" style={{ color: isActive ? color : '#71717A' }}>
                    {mode}
                  </Text>
                  {isActive && (
                    <Text size="xs" fw={600} style={{ color }}>● active</Text>
                  )}
                </Group>
                <Text size="xs" c="#52525B" style={{ wordBreak: 'break-all' }}>{url}</Text>
              </UnstyledButton>
            );
          })}
        </Stack>
      </Modal>
    </>
  );
}
